export interface GhanaPostAddress {
  type: "ghana_post";
  code: string;
}

/**
 * The address shape that matches how deliveries actually work in Ghana:
 * area + landmark directions are what drivers navigate by, an exact GPS pin
 * (captured via "use my location") beats any typed code, and the GhanaPost
 * code is optional because most customers don't know theirs — requiring it
 * only produced fabricated codes.
 */
export interface DetailedAddress {
  type: "detailed";
  area: string;
  landmark: string;
  ghanaPostCode: string | null;
  lat: number | null;
  lng: number | null;
}

// ghana_post remains valid for records created before the detailed format.
export type DeliveryAddress = GhanaPostAddress | DetailedAddress;

// Middle segment is 3 or 4 digits: e.g. CX-689-6150 or GS-0816-2693
export const GHANA_POST_REGEX = /^[A-Z]{2}-\d{3,4}-\d{4}$/;

export function formatGhanaPostInput(raw: string): string {
  const cleaned = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
  if (cleaned.length <= 2) {
    return cleaned;
  }
  if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
  }
  const midEnd = cleaned.length === 10 ? 6 : 5;
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, midEnd)}-${cleaned.slice(midEnd)}`;
}

export function isValidGhanaPostCode(code: string): boolean {
  return GHANA_POST_REGEX.test(code);
}

export function makeGhanaPostAddress(code: string): GhanaPostAddress {
  return { type: "ghana_post", code };
}

export function makeDetailedAddress(input: {
  area: string;
  landmark: string;
  ghanaPostCode?: string | null;
  lat?: number | null;
  lng?: number | null;
}): DetailedAddress {
  return {
    type: "detailed",
    area: input.area,
    landmark: input.landmark,
    ghanaPostCode:
      input.ghanaPostCode != null && input.ghanaPostCode !== ""
        ? input.ghanaPostCode
        : null,
    lat: input.lat ?? null,
    lng: input.lng ?? null,
  };
}

/** One-line summary for lists and detail rows. */
export function formatDeliveryAddress(address: DeliveryAddress): string {
  if (address.type === "ghana_post") {
    return address.code;
  }
  const parts = [address.area, address.landmark].filter((p) => p !== "");
  if (address.ghanaPostCode != null) {
    parts.push(address.ghanaPostCode);
  }
  return parts.join(" — ");
}

/**
 * Google Maps link for driver handoff. Only a captured GPS pin produces a
 * reliable link — GhanaPost codes can't be geocoded without the (closed)
 * official API.
 */
export function deliveryAddressMapsUrl(
  address: DeliveryAddress,
): string | null {
  if (
    address.type === "detailed" &&
    address.lat != null &&
    address.lng != null
  ) {
    return `https://www.google.com/maps?q=${address.lat},${address.lng}`;
  }
  return null;
}
